<template>
  <div>
    <ValidationObserver ref="observer">
      <template #default="{ handleSubmit }">
        <form @submit.prevent class="grid gap-4">
          <div class="grid grid-flow-col gap-2 justify-start">
            <label
              for="file"
              class="px-4 py-2 leading-4 text-xs rounded bg-blue-600 border border-transparent text-white hover:bg-blue-500 focus:border-blue-700 focus:outline-none focus:ring ring-blue-500 ring-opacity-50"
            >
              Select files...
            </label>

            <input
              id="file"
              type="file"
              ref="file"
              multiple
              class="hidden"
              :accept="accept.join(',')"
              @change="onChangeFile($event.target.files)"
            />

            <button
              v-if="hasMedia"
              type="button"
              class="px-4 py-2 leading-4 text-xs rounded bg-transparent border border-transparent text-gray-700 hover:bg-gray-300 hover:text-gray-900 focus:border-gray-500 focus:outline-none focus:bg-gray-300"
              @click="media = {}"
            >
              Clear Files
            </button>
          </div>

          <ul class="grid gap-2">
            <template v-if="hasMedia">
              <li v-for="medium in media" :key="medium.id" class="border rounded px-4 py-3">
                <div class="flex -mx-4">
                  <div class="flex-1 px-4 text-sm" v-text="medium.file.name" />
                  <div v-if="medium.pending" class="flex-none px-4">
                    Uploading {{ medium.progress }}%
                  </div>
                </div>
              </li>
            </template>
            <li v-else class="border border-dotted rounded px-4 py-3 text-sm">
              Select files to upload.
            </li>
          </ul>

          <ul v-if="hasResolvedMedia" class="grid gap-2">
            <li v-for="medium in resolvedMedia" :key="medium.id" class="border rounded px-4 py-3">
              <div class="flex -mx-4">
                <div class="flex-1 px-4 text-sm">
                  {{ medium.file.name }}
                </div>
                <div class="flex-none px-4 text-green-600 text-sm">Completed</div>
              </div>
            </li>
          </ul>

          <ul v-if="hasRejectedMedia" class="grid gap-2">
            <li v-for="medium in rejectedMedia" :key="medium.id" class="border rounded px-4 py-3">
              <div class="flex -mx-4">
                <div class="flex-1 px-4" v-text="medium.file.name" />
                <div class="flex-none px-4 text-red-600 text-sm">Failed</div>
              </div>
            </li>
          </ul>

          <div class="grid grid-flow-col gap-2 justify-start">
            <button
              type="button"
              class="px-4 py-2 leading-4 text-xs rounded bg-green-600 border border-transparent text-white hover:bg-green-500 focus:border-green-700 focus:outline-none focus:ring ring-green-500 ring-opacity-50 disabled:bg-gray-600"
              :disabled="!hasMedia"
              @click="handleSubmit(onSubmit)"
            >
              Upload
            </button>

            <button
              type="button"
              class="px-4 py-2 leading-4 text-xs rounded bg-transparent border border-transparent text-gray-700 hover:bg-gray-300 hover:text-gray-900 focus:border-gray-500 focus:outline-none focus:bg-gray-300"
              @click="cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </template>
    </ValidationObserver>
  </div>
</template>

<script>
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

function generateBlobFromFile(file) {
  if (file.type.substr(0, 6) !== 'image/') {
    return '';
  }

  const URL = window.URL || window.webkitURL;

  if (!URL) {
    return '';
  }

  if (!URL.createObjectURL) {
    return '';
  }

  return URL.createObjectURL(file);
}

export default {
  name: 'UploadForm',

  mixins: [veeValidateErrorsMixin],

  props: {
    productionOrderItemId: {
      type: String,
      required: true,
    },
    accept: {
      type: Array,
      default: () => ['image/x-png', 'image/gif', 'image/jpeg', 'application/pdf'],
    },
  },

  data() {
    return {
      uploading: false,
      media: {},
      resolvedMedia: {},
      rejectedMedia: {},
      errorMsg: '',
    };
  },

  computed: {
    hasMedia() {
      return Object.keys(this.media).length > 0;
    },
    hasResolvedMedia() {
      return Object.keys(this.resolvedMedia).length > 0;
    },
    hasRejectedMedia() {
      return Object.keys(this.rejectedMedia).length > 0;
    },
  },

  watch: {
    media: {
      deep: true,
      handler(media) {
        if (Object.keys(media).length === 0) {
          this.$emit('upload:completed');
        }
      },
    },
  },

  methods: {
    onChangeFile(fileList) {
      Array.from(fileList).forEach((file) => {
        if (!this.accept.indexOf(file.type) === -1) {
          return;
        }

        const id = Math.random().toString(36).substr(2);
        const blob = generateBlobFromFile(file);

        this.media = {
          ...this.media,
          [id]: {
            id,
            file,
            blob,
            thumb: blob,
            data: {
              order_item_id: this.productionOrderItemId,
            },
            progress: 0,
            pending: false,
            resolved: false,
            rejected: false,
          },
        };
      });

      this.$refs.file.value = '';
    },

    onSubmit() {
      const vm = this;
      Object.keys(this.media).forEach((key) => {
        const media = vm.media[key];

        const config = {
          onUploadProgress(progressEvent) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            media.progress = progress;
          },
        };

        const onResolved = (id) => {
          media.pending = false;
          media.resolved = true;
          const { [media.id]: resolvedMedia, ...restMedia } = vm.media;
          vm.resolvedMedia = { ...vm.resolvedMedia, [media.id]: resolvedMedia };
          vm.media = { ...restMedia };
          vm.$emit('uploaded', id);
        };

        const onRejected = () => {
          media.pending = false;
          media.rejected = true;
          const { [media.id]: rejectedMedia, ...restMedia } = vm.media;
          vm.rejectedMedia = { ...vm.rejectedMedia, [media.id]: rejectedMedia };
          vm.media = { ...restMedia };
        };

        media.pending = true;

        const body = new FormData();
        body.append('file', media.file);

        Object.keys(media.data).forEach((dataKey) => {
          const value = media.data[dataKey];

          if (typeof value === 'object' && value !== null) {
            body.append(dataKey, JSON.stringify(value));
            return;
          }

          body.append(dataKey, value);
        });

        this.uploadMedia(body, config, onResolved, onRejected);
      });
    },

    setCreating(value) {
      this.uploading = value;
      this.$emit('uploading', value);
    },
    async uploadMedia(body, config, resolved, rejected) {
      try {
        const { id } = await this.$store.dispatch({
          type: 'ui/media/upload',
          body,
          config,
        });

        resolved(id);
      } catch (error) {
        rejected(error);
      }
    },
    cancel() {
      this.$emit('cancel');
    },
    applyServerErrors(errors) {
      this.observer = this.$refs.observer;
      this.addServerErrors(errors);
    },
  },
};
</script>
